<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'

const MAX_LABEL_LENGTH = 50

const props = withDefaults(
  defineProps<{
    calculationId: number
    suggestions: string[]
    readonly?: boolean
  }>(),
  { readonly: false }
)

const emit = defineEmits<{
  renamed: [payload: { from: string; to: string }]
}>()

const quantityBuildingBlockStore = useQuantityBuildingBlockStore()

const from = ref<string | null>(null)
const to = ref('')
const renaming = ref(false)

const canRename = computed(() => {
  const next = to.value.trim()
  return (
    !props.readonly &&
    !!from.value &&
    next.length > 0 &&
    next.length <= MAX_LABEL_LENGTH &&
    next !== from.value
  )
})

const applyRename = async () => {
  if (!canRename.value || !from.value) return
  const payload = { from: from.value, to: to.value.trim() }
  renaming.value = true
  try {
    await quantityBuildingBlockStore.renameLabel(props.calculationId, payload.from, payload.to)
    from.value = null
    to.value = ''
    emit('renamed', payload)
  } finally {
    renaming.value = false
  }
}
</script>

<template>
  <div class="mb-4">
    <div class="text-subtitle-2 mb-1">Rename a label everywhere</div>
    <v-row dense align="center">
      <v-col cols="12" sm="5">
        <v-select
          v-model="from"
          :items="suggestions"
          label="Label"
          density="comfortable"
          hide-details
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" sm="5">
        <v-text-field
          v-model="to"
          label="New name"
          :maxlength="MAX_LABEL_LENGTH"
          density="comfortable"
          hide-details
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" sm="2">
        <v-btn
          color="primary"
          variant="tonal"
          :loading="renaming"
          :disabled="!canRename"
          @click="applyRename"
        >
          Rename
        </v-btn>
      </v-col>
    </v-row>
    <div class="text-caption text-medium-emphasis mt-1">
      Renames the label across <strong>all rows in this calculation</strong> — the bars/stacking
      grouped under it stay the same, only the name changes. Applies immediately.
    </div>
  </div>
</template>
